// src/pages/PatientTodayPage.jsx
// Route: /patient/today
//
// The light-assistant-mode landing screen (PatientSettingsPage's "Light"
// option) — a status-first summary shown post-login instead of jumping
// straight into Ask Previu chat. The chat screen (/patient/chat,
// "Ask Previu" in the sidebar) stays reachable separately, and stays
// the only place for the care-plan/bio-age stat cards and the book
// appointment/order lab test/view care plan quick actions — see
// ChatWindow.jsx/WelcomeBubble.jsx's isLightMode handling. This page
// intentionally does not duplicate them.
//
// This file only fetches the data (summary/journey) and
// handles loading states; the screen itself is
// PatientTodayView, shared by both portal appearance themes (see its own
// header comment) — formatShortDate/AskPreviuBar below are exported for it
// to reuse rather than duplicating either.
//
// Data sources, all read-only, no new writes from this page:
//   - fetchPatientSummary (services/patientSummary.js) — triage history,
//     care plan, previu age (still used here for the "care plan updated"
//     checklist item and the records summary bar). Shared with ChatPage's
//     welcome bubble.
//   - healthJourneyAPI.get — the timeline + latest/next milestone
//     (backend: HealthJourneyService, computed from registration date +
//     triage history + upcoming appointments; nothing new is stored).

import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { PatientAppShell } from '../components/PatientLayout';
import { usePatient } from '../context/PatientContext';
import { fetchPatientSummary } from '../services/patientSummary';
import { healthJourneyAPI, labTestOrderAPI } from '../services/api';
import { labOrdersFrom, labTestReminders } from '../utils/labTestReminders';
import Icon from '../components/Icon';
import PatientTodayView from './PatientTodayView';
import PatientTodaySimpleView from './PatientTodaySimpleView';

export function formatShortDate(dateStr) {
  if (!dateStr) return '';
  return new Date(dateStr).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
}

// ─── Ask Previu input — hands off to the chat screen ─────────────────────────
export function AskPreviuBar({ placeholder = 'Ask about your health story…' }) {
  const navigate = useNavigate();
  const [value, setValue] = useState('');

  function submit() {
    const trimmed = value.trim();
    if (trimmed) {
      sessionStorage.setItem('previu_chat_prompt', trimmed);
      // The patient's own typed query — it should land already sent, not
      // sitting in the textarea a second time. Also used on the result page.
      sessionStorage.setItem('previu_chat_autosubmit', '1');
    }
    navigate('/patient/chat');
  }

  return (
    <div className="card" style={{ display: 'flex', alignItems: 'center', gap: 10, margin: 0, padding: '10px 10px 10px 16px' }}>
      <Icon name="chat" size={16} />
      <input
        value={value}
        onChange={e => setValue(e.target.value)}
        onKeyDown={e => { if (e.key === 'Enter') submit(); }}
        placeholder={placeholder}
        style={{
          flex: 1, border: 'none', outline: 'none', background: 'transparent',
          fontSize: 14, fontFamily: 'var(--font-body)', color: 'var(--text-primary)',
        }}
      />
      <button
        type="button"
        onClick={submit}
        aria-label="Ask Previu"
        className="btn btn-accent"
        style={{ borderRadius: '50%', width: 34, height: 34, padding: 0, flexShrink: 0 }}
      >
        <Icon name="chat" size={14} />
      </button>
    </div>
  );
}

function TodaySkeleton() {
  return (
    <div>
      <style>{`@keyframes today-pulse{0%,100%{opacity:.55}50%{opacity:1}}`}</style>
      {[28, 180, 220].map((h, i) => (
        <div key={i} style={{
          height: h, background: 'var(--bg-hover)', borderRadius: 12,
          marginBottom: 14, animation: 'today-pulse 1.4s ease-in-out infinite',
        }} />
      ))}
    </div>
  );
}

export default function PatientTodayPage() {
  const { patient, profile } = usePatient();

  const [summary, setSummary] = useState(null); // null = loading
  const [journey, setJourney] = useState(null);
  // Non-blocking: the screen renders without it; a failed fetch just means no lab reminder.
  const [labOrders, setLabOrders] = useState([]);

  const fetchedRef = useRef(false);

  useEffect(() => {
    if (fetchedRef.current) return;
    if (!patient?.id) return;
    fetchedRef.current = true;
    const token = localStorage.getItem('previu_token');
    if (!token) return;

    fetchPatientSummary(patient.id, token)
      .then(setSummary)
      .catch(() => setSummary({
        lastResult: null, openActionItems: 0, totalActionItems: 0,
        previuAge: null, previuAgeDelta: null, triageHistoryCount: 0,
      }));

    healthJourneyAPI.get(patient.id)
      .then(res => setJourney(res.data))
      .catch(err => {
        console.error('PatientTodayPage: health-journey fetch failed', err);
        setJourney({ startedAt: null, milestones: [], latestMilestone: null, nextMilestone: null });
      });

    labTestOrderAPI.list({ patientId: patient.id, size: 20 })
      .then(res => setLabOrders(labOrdersFrom(res.data)))
      .catch(() => setLabOrders([]));
  }, [patient?.id]);

  if (!patient) return null; // PatientRoute already guards this route

  const loading = summary === null || journey === null;
  // Settings → Today screen. METRIC (the original screen) unless the patient
  // chose SIMPLIFIED; an unknown/missing value falls back to METRIC.
  const TodayView = profile?.todayView === 'SIMPLIFIED' ? PatientTodaySimpleView : PatientTodayView;

  return (
    <PatientAppShell>
      {loading
        ? <TodaySkeleton />
        : <TodayView patient={patient} summary={summary} journey={journey}
            labReminders={labTestReminders(labOrders)} />}
    </PatientAppShell>
  );
}
